import React, { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Package, Zap, Droplet, ShieldCheck, Leaf, Expand as ExpandIcon } from 'lucide-react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { AmbientLight, DirectionalLight } from 'three';
import ModelComponent from './ModelComponent';
import SceneViewer from '../scene_studio/viewer/SceneViewer';
import waterFlowSceneConfig from '../scene_studio/configs/water_flow_simulation_scene_config.json';
import SimpleModelViewer from './SimpleModelViewer';
import VideoPlayer from './VideoPlayer';
import './ModelShowcaseSection.css';

function ModelShowcaseSection() {
  const { t } = useTranslation();

  // Model for the new full-width viewer
  const fullWidthModelPath = "/models/sy3_kapsel.glb";
  
  // Model for the interactive demo card (SceneViewer)
  const interactiveDemoModelPath = "/models/waterjet.glb";
  
  // Commented out the old logic
  // const modelPath = "/models/sy3_kapsel.glb";
  // const modelPath = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  //   ? "/models/model_mobile.glb"
  //   : "/models/model.glb";

  // Preload video for better performance
  React.useEffect(() => {
    const preloadVideo = async () => {
      try {
        console.log('ModelShowcaseSection: Starting video preload for waterflow.mp4');
        const response = await fetch('/videos/waterflow.mp4', {
          method: 'HEAD',
          headers: {
            'Accept': 'video/mp4'
          }
        });
        
        if (response.ok) {
          console.log('ModelShowcaseSection: Video preload successful, file size:', response.headers.get('content-length'));
        } else {
          console.warn('ModelShowcaseSection: Video preload failed with status:', response.status);
        }
      } catch (error) {
        console.error('ModelShowcaseSection: Video preload error:', error);
      }
    };
    
    preloadVideo();
  }, []);

  const SceneLoader = () => (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: 'var(--color-primary)' }}>
      Loading 3D Scene...
    </div>
  );

  return (
    <section 
      id="model-showcase" 
      className="model-showcase content-panel"
    >
      <div className="text-content text-center">
        <h2 className="model-showcase-headline">{t('joinus_modelShowcaseTitle')}</h2>
        <p className="section-tagline prominent-subheadline">
          {t('modelShowcaseTagline')}
        </p>
      </div>

      <div className="full-width-model-viewer" style={{ marginBottom: '1.5rem' }}>
        <Suspense fallback={<SceneLoader />}>
          <SimpleModelViewer modelPath={fullWidthModelPath} />
        </Suspense>
      </div>

      <div className="features-grid horizontal" style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}>
        <div className="feature-item">
          <Droplet size={30} className="icon" />
          <h3>{t('modelShowcaseFeature1Title')}</h3>
          <p>{t('modelShowcaseFeature1Desc')}</p>
        </div>
        <div className="feature-item">
          <Zap size={30} className="icon" />
          <h3>{t('modelShowcaseFeature2Title')}</h3>
          <p>{t('modelShowcaseFeature2Desc')}</p>
        </div>
        <div className="feature-item">
          <ShieldCheck size={30} className="icon" />
          <h3>{t('modelShowcaseFeature3Title')}</h3>
          <p>{t('modelShowcaseFeature3Desc')}</p>
        </div>
        <div className="feature-item">
          <Leaf size={30} className="icon" />
          <h3>{t('modelShowcaseFeature4Title')}</h3>
          <p>{t('modelShowcaseFeature4Desc')}</p>
        </div>
      </div>

      <div className="action-cards-container" style={{ marginBottom: '1.5rem' }}>
        <div className="action-card animation-card">
          <div className="card-content">
            <Suspense fallback={<SceneLoader />}>
              <SceneViewer
                sceneConfig={waterFlowSceneConfig}
                modelPath={interactiveDemoModelPath}
              />
            </Suspense>
          </div>
          <div className="card-overlay">
            <div className="overlay-text-content">
              <h3>Interactive Demo</h3>
              <p className="overlay-description">A design study of the SY3</p>
            </div>
          </div>
        </div>
        <div className="action-card video-card">
          <div className="card-content">
            <VideoPlayer 
              src="/videos/sy3outboard.mp4"
              autoPlay
              muted
              loop
            />
          </div>
          <div className="card-overlay">
            <div className="overlay-text-content">
              <h3>Product Video</h3>
              <p className="overlay-description">The SY3-1000 mounted on a small boat</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ModelShowcaseSection; 